/**
 * FileManagerProposalCard
 *
 * Specialised ProposalCard rendered when the AoA fires
 * `propose_manage_files_on_agent` or `propose_manage_files_on_skill`.
 *
 * UX contract (decided by the user 2026-06-09, tightened 2026-06-10):
 *
 *   1. Card shows the CURRENT attached files snapshot the propose tool
 *      captured server-side. Each row has a small remove-on-apply
 *      toggle (a tiny "×" — not a checkbox).
 *   2. A drop zone underneath lets the user stage NEW files. Validation
 *      is done by the server when each file uploads — same as the
 *      regular skill / agent file upload UI.
 *   3. Staged files are remembered ACROSS panel navigation — the user
 *      can drop a file, click somewhere else, come back, and still
 *      see what they staged. State lives in InspectorContext keyed by
 *      proposal_id so a remount restores it.
 *   4. Nothing happens server-side until the user clicks "Upload &
 *      Apply". On click, the card:
 *        - POSTs each new file to the existing /chatbots/<id>/attachments
 *          (or /skills/<id>/files) endpoint.
 *        - DELETEs each marked-for-removal attachment.
 *        - Records the actual outcome by resolving the proposal with
 *          decision='edit' + edited_payload carrying the result.
 *      Auto-continuation then tells the model what was added/removed.
 *   5. Reject = nothing changes. The card collapses.
 *
 *   IMPORTANT — the ONLY way to add a file is via this drop zone.
 *   Backend deduplication via file_hash handles the case where the
 *   user uploads a file that's already in the system.
 *
 * Visual idiom: borrowed from the Edit-Agent page's BotContextFiles
 * component — same UploadArea, same file-row style — so the card
 * feels like a familiar tool the user already knows.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { Alert, Switch, Tooltip, message as antdMessage } from 'antd';

import type { AoAProposal, ProposalResolution } from '../types';
import { aoaApi } from '../services/aoaApi';
import { useInspector } from './InspectorContext';
// botService is a default-exported SINGLETON.
import botService from '../../chatbots/services/botService';
import { SkillsApi } from '../../skills/services/skillsApi';
import { DataBundleApi } from '../../data-bundles/services/dataBundleService';
import { vaultService } from '../../vault/vaultService';
import { UploadArea } from '@/components/common';
import './ProposalCard.css';
import './FileManagerProposalCard.css';

interface AttachmentSnapshot {
  attachment_id: string;
  file_id: string;
  file_name: string;
  file_size_bytes: number | null;
  processing_status: string | null;
  include_as_context: boolean;
}

interface FileManagerPayload {
  target_type: 'agent' | 'skill' | 'data_bundle' | 'tenant' | 'folder';
  target_id: string;
  target_name: string;
  attachments_snapshot: AttachmentSnapshot[];
}

interface Props {
  proposal: AoAProposal;
  onResolved?: (resolution: ProposalResolution, updated: AoAProposal) => void;
}

/**
 * Same allowlist BotContextFiles uses — mirrors the platform default
 * and the backend's chunkable-text MIME allowlist. Excel / PowerPoint
 * deliberately omitted: those use dedicated tools.
 */
const ALLOWED_EXTENSIONS = ['.txt', '.md', '.csv', '.json', '.doc', '.docx', '.pdf', '.pages'];

function formatSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function isAllowedExtension(filename: string): boolean {
  const ext = '.' + (filename.split('.').pop() || '').toLowerCase();
  return ALLOWED_EXTENSIONS.includes(ext);
}

/**
 * SVG file-type icon — mirrors BotContextFiles' FileTypeIcon so file
 * rows in the AoA card look identical to the Edit-Agent file list.
 * Inlined here (instead of importing from chatbots/) so this card
 * doesn't take a circular module dependency on the bots feature.
 */
function FileTypeIcon({ fileName }: { fileName: string }) {
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  const isPdf = ext === 'pdf';
  const isWord = ext === 'doc' || ext === 'docx' || ext === 'pages';
  const isSheet = ext === 'csv' || ext === 'xlsx' || ext === 'xls' || ext === 'numbers';
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="14 2 14 8 20 8" strokeLinecap="round" strokeLinejoin="round" />
      {(isPdf || isWord) && (
        <>
          <line x1="16" y1="13" x2="8" y2="13" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="16" y1="17" x2="8" y2="17" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
      {isSheet && (
        <>
          <rect x="8" y="12" width="8" height="6" rx="1" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="12" y1="12" x2="12" y2="18" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="8" y1="15" x2="16" y2="15" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
    </svg>
  );
}

export const FileManagerProposalCard: React.FC<Props> = ({ proposal, onResolved }) => {
  const { t } = useLanguage();
  const payload = (proposal.proposed_payload || {}) as unknown as FileManagerPayload;
  const targetType = payload.target_type;

  const targetId = payload.target_id;

  // Per-file "use as context" toggle. Written once: these two call sites used
  // to be hand-rolled 3-branch ternaries with the TENANT endpoint as the else,
  // so when data_bundle was added it silently PATCHed the tenant instead of the
  // bundle. A bundle has no per-file context flag, so it is a no-op here rather
  // than a wrong write.
  const setIncludeAsContext = useCallback(
    async (attachmentId: string, include: boolean): Promise<any> => {
      if (targetType === 'agent') {
        return botService.updateBotAttachment(targetId, attachmentId,
          { include_as_context: include });
      }
      if (targetType === 'skill') {
        return SkillsApi.updateSkillFile(targetId, attachmentId,
          { include_as_context: include });
      }
      if (targetType === 'tenant') {
        return aoaApi.updateTenantContextFile(targetId, attachmentId,
          { include_as_context: include });
      }
      return { include_as_context: include };
    },
    [targetType, targetId],
  );
  const targetName = payload.target_name || String(targetType || 'entity');
  // attachments_snapshot is the LIST AT PROPOSAL-EMIT TIME. After the
  // user clicks Upload & Apply (or anytime they reopen the card), it's
  // stale. We refetch fresh state from the same endpoints the
  // Edit-Agent / SkillModal use, and prefer that over the snapshot
  // when it's available.
  const snapshotAttachments = payload.attachments_snapshot || [];
  const [liveAttachments, setLiveAttachments] = useState<AttachmentSnapshot[] | null>(null);
  const initialAttachments: AttachmentSnapshot[] =
    liveAttachments ?? snapshotAttachments;

  const inspector = useInspector();
  // Staged files are remembered on the inspector so the user can navigate
  // away from this card and come back without losing what they dropped.
  // Local state is just the inspector slice for this proposal.
  const stagedFiles = inspector.stagedFilesByProposalId[proposal.proposal_id] ?? [];

  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Existing attachments the user has marked for removal.
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  // Existing attachments whose include_as_context the user wants to
  // flip. Key = attachment_id, value = the DESIRED new state. Cleared
  // entries mean "no change". Only existing files use this map —
  // newly-staged files use stagedContextFlags below.
  const [contextChanges, setContextChanges] = useState<Record<string, boolean>>({});
  // Per-staged-file desired include_as_context state. Key = stable
  // composite of file name + size (same as the de-dupe key elsewhere).
  // Default off; user opts in.
  const [stagedContextFlags, setStagedContextFlags] = useState<Record<string, boolean>>({});
  const [localStatus, setLocalStatus] = useState<AoAProposal['status']>(proposal.status);

  useEffect(() => setLocalStatus(proposal.status), [proposal.status]);
  const isResolved = localStatus !== 'pending';

  // Re-pull the live attachment list from the same endpoint the
  // Edit-Agent / SkillModal use, so the rendered "Currently attached"
  // section always reflects the DB — not the stale proposal-time
  // snapshot. Runs on mount AND every time localStatus changes (e.g.
  // post-apply re-render, status flip to 'edited' / 'approved').
  const refreshLiveAttachments = useCallback(async () => {
    try {
      if (targetType === 'agent') {
        const rows = await botService.getBotAttachments(targetId);
        setLiveAttachments(rows.map((r: any) => ({
          attachment_id: String(r.bot_attachment_id),
          file_id: String(r.file_id),
          file_name: r.file?.file_name || 'unknown',
          file_size_bytes: r.file?.file_size_bytes ?? null,
          processing_status: r.file?.processing_status ?? null,
          include_as_context: Boolean(r.include_as_context),
        })));
      } else if (targetType === 'skill') {
        const rows = await SkillsApi.getSkillFiles(targetId);
        setLiveAttachments(rows.map((r: any) => ({
          attachment_id: String(r.skill_attachment_id),
          file_id: String(r.file_id),
          file_name: r.file?.file_name || r.file_name || 'unknown',
          file_size_bytes: r.file?.file_size_bytes ?? r.file_size_bytes ?? null,
          processing_status: r.file?.processing_status ?? r.processing_status ?? null,
          include_as_context: Boolean(r.include_as_context),
        })));
      } else if (targetType === 'data_bundle') {
        // Data bundle — the bundle row carries its own file list.
        const bundle: any = await DataBundleApi.getBundle(targetId);
        setLiveAttachments((bundle?.files || []).map((r: any) => ({
          // The bundle's delete endpoint keys on FILE id, not a join-row id.
          attachment_id: String(r.file_id),
          file_id: String(r.file_id),
          file_name: r.file_name || r.file?.file_name || 'unknown',
          file_size_bytes: r.file_size_bytes ?? r.file?.file_size_bytes ?? null,
          processing_status: r.processing_status ?? r.file?.processing_status ?? null,
          include_as_context: Boolean(r.include_as_context ?? true),
        })));
      } else if (targetType === 'folder') {
        // Library folder — placements come off the folder read. Like the
        // bundle variant, the remove endpoint keys on FILE id. There is no
        // per-file context flag here: "always in context" lives on the
        // agent LINK, not the file, so the toggle is hidden for folders.
        const folder = await vaultService.getFolder(targetId);
        setLiveAttachments((folder.files || []).map((r: any) => ({
          attachment_id: String(r.file_id),
          file_id: String(r.file_id),
          file_name: r.file_display_name || r.file_name || 'unknown',
          file_size_bytes:
            r.file_size_mb != null ? Math.round(r.file_size_mb * 1024 * 1024) : null,
          processing_status: null,
          include_as_context: false,
        })));
      } else {
        // tenant — files live on tenant_attachments (att.to_dict() shape).
        const rows = await aoaApi.listTenantContextFiles(targetId);
        setLiveAttachments(rows.map((r: any) => ({
          attachment_id: String(r.tenant_attachment_id),
          file_id: String(r.file_id),
          file_name: r.file_name || r.attachment_filename || 'unknown',
          file_size_bytes: r.file_size_bytes ?? null,
          processing_status: r.processing_status ?? null,
          include_as_context: Boolean(r.include_as_context),
        })));
      }
    } catch (e) {
      // Non-fatal — fall back to the snapshot. The user just sees
      // proposal-time state instead of fresh.
      console.warn('[AoA FileManagerProposalCard] live refresh failed', e);
    }
  }, [targetType, targetId]);

  useEffect(() => {
    void refreshLiveAttachments();
    // Re-fetch on status flip too: a resolution might happen in another
    // tab / via the AoA, and we want this card to reflect the latest.
  }, [refreshLiveAttachments, localStatus]);

  const toggleRemove = useCallback((attachmentId: string) => {
    setRemovedIds(prev => {
      const next = new Set(prev);
      if (next.has(attachmentId)) next.delete(attachmentId);
      else next.add(attachmentId);
      return next;
    });
  }, []);

  // Resolve the CURRENT visible context-toggle state for an existing
  // attachment: the desired override if set, otherwise the snapshot's
  // baseline. The diff between this and the baseline is what apply
  // pushes to the backend.
  const resolveExistingContextState = useCallback(
    (att: AttachmentSnapshot): boolean => {
      if (att.attachment_id in contextChanges) return contextChanges[att.attachment_id];
      return Boolean(att.include_as_context);
    },
    [contextChanges],
  );

  const toggleExistingContext = useCallback(
    (att: AttachmentSnapshot, next: boolean) => {
      console.info('[AoA file-manager] toggle existing direct-to-context', {
        attachment_id: att.attachment_id,
        file: att.file_name,
        was_in_snapshot: Boolean(att.include_as_context),
        wants_to_become: next,
      });
      setContextChanges((prev) => {
        const baseline = Boolean(att.include_as_context);
        const copy = { ...prev };
        if (next === baseline) {
          // Back to original — drop the override entirely so the apply
          // step doesn't PATCH a no-op call.
          delete copy[att.attachment_id];
        } else {
          copy[att.attachment_id] = next;
        }
        return copy;
      });
    },
    [],
  );

  // Same composite key the de-dupe in stageFiles uses, so the flag
  // map stays correctly addressed across re-renders.
  const stagedKey = (f: File): string => `${f.name}:${f.size}`;
  const resolveStagedContextState = useCallback(
    (f: File): boolean => Boolean(stagedContextFlags[stagedKey(f)]),
    [stagedContextFlags],
  );
  const toggleStagedContext = useCallback((f: File, next: boolean) => {
    setStagedContextFlags((prev) => ({ ...prev, [stagedKey(f)]: next }));
  }, []);

  /**
   * Stage one or more dropped/picked files. Validates extension only —
   * the server enforces size + MIME on upload-and-apply. Skipped files
   * get a toast so the user knows something didn't land.
   */
  const stageFiles = useCallback((files: FileList | File[]) => {
    const incoming = Array.from(files);
    const accepted: File[] = [];
    const rejected: string[] = [];
    for (const f of incoming) {
      if (isAllowedExtension(f.name)) accepted.push(f);
      else rejected.push(f.name);
    }
    if (rejected.length > 0) {
      antdMessage.error(
        `Skipped ${rejected.length} unsupported file(s): ${rejected.join(', ')}. ` +
        `Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`,
      );
    }
    if (accepted.length === 0) return;
    // De-dupe by name+size so the user can drop the same file twice
    // without seeing it appear twice in the staged list.
    const seen = new Set(stagedFiles.map((f) => `${f.name}:${f.size}`));
    const filtered = accepted.filter((f) => !seen.has(`${f.name}:${f.size}`));
    inspector.setStagedFiles(proposal.proposal_id, [...stagedFiles, ...filtered]);
  }, [stagedFiles, inspector, proposal.proposal_id]);

  const removeStagedFile = useCallback((index: number) => {
    const next = stagedFiles.filter((_, i) => i !== index);
    if (next.length === 0) inspector.clearStagedFiles(proposal.proposal_id);
    else inspector.setStagedFiles(proposal.proposal_id, next);
  }, [stagedFiles, inspector, proposal.proposal_id]);

  // Drag-and-drop wiring for UploadArea. `dragOver` is purely visual.
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (isResolved || busy) return;
    setDragOver(true);
  }, [isResolved, busy]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (isResolved || busy) return;
    if (e.dataTransfer.files.length > 0) {
      stageFiles(e.dataTransfer.files);
    }
  }, [isResolved, busy, stageFiles]);

  // Pending = anything that, on apply, will mutate state server-side.
  // Includes new uploads, removals, AND existing-attachment context
  // toggle flips.
  const hasPendingChanges =
    stagedFiles.length > 0 ||
    removedIds.size > 0 ||
    Object.keys(contextChanges).length > 0;

  const handleUploadAndApply = useCallback(async () => {
    setBusy(true);
    setError(null);
    const attachedFileIds: string[] = [];
    const removedAttachmentIds: string[] = [];
    const errors: string[] = [];

    try {
      // 1. Upload new files to the EXISTING endpoint. Sequentially —
      //    a hard failure on file 3 of 5 shouldn't leave the user
      //    wondering what landed. Track each upload's returned
      //    bot_attachment_id so step 1b can flip context per-file.
      const uploadedAttachments: Array<{
        bot_attachment_id: string;
        file: File;
        wantContext: boolean;
      }> = [];
      for (let i = 0; i < stagedFiles.length; i++) {
        const f = stagedFiles[i];
        try {
          // Surface progress as "file i of N" plus the per-file %.
          if (targetType === 'agent') {
            const att = await botService.uploadBotFile(
              targetId,
              f,
              (pct) => setUploadProgress(((i + pct / 100) / stagedFiles.length) * 100),
            );
            attachedFileIds.push(att.file_id);
            uploadedAttachments.push({
              bot_attachment_id: att.bot_attachment_id,
              file: f,
              wantContext: resolveStagedContextState(f),
            });
          } else if (targetType === 'skill') {
            const att = await SkillsApi.uploadSkillFile(targetId, f);
            attachedFileIds.push(att.file_id);
            setUploadProgress(((i + 1) / stagedFiles.length) * 100);
            uploadedAttachments.push({
              bot_attachment_id: att.skill_attachment_id,
              file: f,
              wantContext: resolveStagedContextState(f),
            });
          } else if (targetType === 'data_bundle') {
            const bf: any = await DataBundleApi.uploadFile(targetId, f);
            attachedFileIds.push(bf.file_id);
            setUploadProgress(((i + 1) / stagedFiles.length) * 100);
            uploadedAttachments.push({
              bot_attachment_id: bf.file_id,
              file: f,
              wantContext: resolveStagedContextState(f),
            });
          } else if (targetType === 'folder') {
            // Library folder — single-call upload+place. Summaries and
            // indexing follow the file automatically (no Save step).
            const { placement } = await vaultService.uploadToFolder(
              targetId,
              f,
              (pct) => setUploadProgress(((i + pct / 100) / stagedFiles.length) * 100),
            );
            attachedFileIds.push(placement.file_id);
            // No context toggle for folders — the flag lives on agent links.
          } else {
            // tenant — same endpoints the tenant-context editor uses, exposed
            // under the AoA namespace. Returns the tenant_attachment row.
            const att = await aoaApi.uploadTenantContextFile(targetId, f);
            attachedFileIds.push(att.file_id);
            setUploadProgress(((i + 1) / stagedFiles.length) * 100);
            uploadedAttachments.push({
              bot_attachment_id: att.tenant_attachment_id,
              file: f,
              wantContext: resolveStagedContextState(f),
            });
          }
        } catch (e: any) {
          errors.push(
            `Upload of "${f.name}" failed: ${e?.response?.data?.error || e?.message || 'unknown'}`,
          );
        }
      }

      // 1b. Flip include_as_context on each freshly-uploaded file that
      //     the user marked. The PATCH endpoint runs the same backend
      //     validation as the Edit-Agent / SkillModal toggle paths —
      //     context-size limits, MIME allowlist, processing-status
      //     gating. If the file isn't ready / isn't eligible, the
      //     backend rejects with a structured error and we surface it.
      //
      //     We TRUST BUT VERIFY: the PATCH response body echoes the
      //     persisted attachment row. If `include_as_context` in the
      //     response isn't what we asked for, the toggle didn't really
      //     change and we throw — better a visible error than a silent
      //     "applied" toast over an unchanged row.
      for (const up of uploadedAttachments) {
        if (!up.wantContext) continue;  // default off — nothing to do
        try {
          console.info('[AoA file-manager] PATCH include_as_context=true (newly uploaded)', {
            target: targetType,
            target_id: targetId,
            attachment_id: up.bot_attachment_id,
            file: up.file.name,
          });
          const result = await setIncludeAsContext(up.bot_attachment_id, true);
          const echoed = (result as any)?.include_as_context;
          console.info('[AoA file-manager] PATCH result', {
            attachment_id: up.bot_attachment_id,
            echoed_include_as_context: echoed,
          });
          if (echoed !== true) {
            errors.push(
              `Server accepted PATCH for "${up.file.name}" but the response shows include_as_context=${echoed}. Toggle did not persist.`,
            );
          }
        } catch (e: any) {
          errors.push(
            `Could not enable context on "${up.file.name}": ${e?.response?.data?.error || e?.message || 'unknown'}`,
          );
        }
      }

      // 2. Remove marked attachments via the EXISTING delete endpoint.
      for (const aid of removedIds) {
        try {
          if (targetType === 'agent') {
            await botService.deleteBotFile(targetId, aid);
          } else if (targetType === 'skill') {
            await SkillsApi.deleteSkillFile(targetId, aid);
          } else if (targetType === 'data_bundle') {
            await DataBundleApi.removeFile(targetId, aid);
          } else if (targetType === 'folder') {
            // attachment_id carries the FILE id for folders (see refresh).
            await vaultService.removeFile(targetId, aid);
          } else {
            await aoaApi.deleteTenantContextFile(targetId, aid);
          }
          removedAttachmentIds.push(aid);
        } catch (e: any) {
          errors.push(
            `Removing attachment failed: ${e?.response?.data?.error || e?.message || 'unknown'}`,
          );
        }
      }

      // 2b. Flip include_as_context on EXISTING attachments the user
      //     toggled. Skip any that were also marked for removal — the
      //     remove already happened above and the PATCH would 404.
      //     Same trust-but-verify: confirm the response echoes the
      //     value we sent. If it doesn't match, raise a visible error
      //     instead of pretending the toggle worked.
      for (const [attId, nextState] of Object.entries(contextChanges)) {
        if (removedIds.has(attId)) continue;
        try {
          console.info('[AoA file-manager] PATCH include_as_context (existing)', {
            target: targetType,
            target_id: targetId,
            attachment_id: attId,
            desired_include_as_context: nextState,
          });
          const result = await setIncludeAsContext(attId, nextState);
          const echoed = (result as any)?.include_as_context;
          console.info('[AoA file-manager] PATCH result', {
            attachment_id: attId,
            desired: nextState,
            echoed_include_as_context: echoed,
            match: echoed === nextState,
          });
          if (echoed !== nextState) {
            errors.push(
              `Server accepted PATCH for attachment ${attId.slice(0, 8)}… but echoed include_as_context=${echoed} (we asked for ${nextState}). Toggle did not persist.`,
            );
          }
        } catch (e: any) {
          errors.push(
            `Could not update context on attachment: ${e?.response?.data?.error || e?.message || 'unknown'}`,
          );
        }
      }

      // 3. Record outcome in the proposal as `edited_payload`. The
      //    backend apply function reads this and the continuation
      //    message tells the model what actually happened — even on
      //    partial failure, the model sees the truth.
      // Total of context flips that hit the server: uploads whose
      // wantContext was on + existing-attachment changes we PATCHed.
      // Used by the AoA continuation message — without this the model
      // would tell the user "0 files changed" even though it just
      // flipped 1.
      const contextChangesApplied =
        uploadedAttachments.filter((u) => u.wantContext).length +
        Object.keys(contextChanges).filter((id) => !removedIds.has(id)).length;
      const updated = await aoaApi.resolveProposal(proposal.proposal_id, {
        action: 'edit',
        // File-manager edits have no single "after value" — the truth is
        // the pair of arrays in edited_payload. Use null so the type
        // matches; the backend's resolve handler reads edited_payload
        // for this tool, not final_value.
        final_value: null,
        edited_payload: {
          attached_file_ids: attachedFileIds,
          removed_attachment_ids: removedAttachmentIds,
          context_changes_count: contextChangesApplied,
          errors,
        },
      });
      setLocalStatus(updated.status);
      // Done — clear staged files so the next time the user sees this
      // proposal it isn't stuck on the (now applied) staging state.
      inspector.clearStagedFiles(proposal.proposal_id);
      // Refetch live attachments NOW so the rendered list reflects
      // what just landed in the DB, not the proposal-time snapshot.
      // Async — don't block the resolve callback on it.
      void refreshLiveAttachments();
      onResolved?.(
        { action: 'edit', edited_payload: {} as any } as ProposalResolution,
        updated,
      );

      const ctxSummary = contextChangesApplied
        ? ', ' + t('counts.contextTogglesApplied', { count: contextChangesApplied })
        : '';
      if (errors.length === 0) {
        antdMessage.success(
          `Applied to ${targetName}: added ${attachedFileIds.length}, removed ${removedAttachmentIds.length}${ctxSummary}.`,
        );
      } else {
        antdMessage.warning(
          `Applied to ${targetName} with ${errors.length} error(s). Added ${attachedFileIds.length}, removed ${removedAttachmentIds.length}${ctxSummary}.`,
        );
        setError(errors.join('\n'));
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || t('metaAgent.fileManager.applyFailed');
      setError(msg);
      antdMessage.error(msg);
    } finally {
      setBusy(false);
      setUploadProgress(undefined);
    }
  }, [
    stagedFiles, removedIds, contextChanges, resolveStagedContextState,
    targetType, targetId, proposal.proposal_id, onResolved, inspector,
  ]);

  const handleReject = useCallback(async () => {
    setBusy(true);
    try {
      const updated = await aoaApi.resolveProposal(proposal.proposal_id, {
        action: 'reject',
        reason: t('metaAgent.fileManager.dismissed'),
      });
      setLocalStatus(updated.status);
      inspector.clearStagedFiles(proposal.proposal_id);
      onResolved?.(
        { action: 'reject', reason: '' } as ProposalResolution,
        updated,
      );
    } catch (e: any) {
      setError(e?.message || t('metaAgent.fileManager.rejectFailed'));
    } finally {
      setBusy(false);
    }
  }, [proposal.proposal_id, onResolved, inspector]);

  const statusBadge = useMemo(() => {
    if (localStatus === 'pending') return null;
    if (localStatus === 'edited' || localStatus === 'approved') {
      return (
        <span className="aoa-proposal-card__badge aoa-proposal-card__badge--applied">
          applied
        </span>
      );
    }
    return (
      <span className="aoa-proposal-card__badge aoa-proposal-card__badge--rejected">
        {localStatus}
      </span>
    );
  }, [localStatus]);

  return (
    <div
      className={`aoa-proposal-card aoa-proposal-card--filemgr${
        isResolved ? ' aoa-pcard--resolved-view' : ''
      }`}
      aria-disabled={isResolved || undefined}
    >
      <div className="aoa-proposal-card__header">
        <span className="aoa-proposal-card__tool-label">
          {targetType === 'folder'
            ? 'manage files in Library folder'
            : `manage files on ${targetType}`}
        </span>
        <span className="aoa-proposal-card__entity-name">{targetName}</span>
        {statusBadge}
      </div>

      {/* Resolved → small lock badge at the top-right; rest of the
          card body fades under .aoa-pcard--resolved-view styling. */}
      {isResolved && (
        <div
          className="aoa-pcard-lock-badge"
          title={
            localStatus === 'rejected' || localStatus === 'timeout'
              ? t('metaAgent.fileManager.rejectedReadOnly')
              : t('metaAgent.fileManager.appliedReadOnly')
          }
          aria-label={t('metaAgent.fileManager.readOnlyResolved')}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
               aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
      )}

      {proposal.rationale && (
        <div className="aoa-proposal-card__rationale">{proposal.rationale}</div>
      )}

      <div className="aoa-proposal-card__section">
        <div className="aoa-proposal-card__section-title">
          {t('metaAgent.fileManager.currentlyAttached')} <span className="aoa-fm__count">({initialAttachments.length})</span>
        </div>
        {initialAttachments.length === 0 ? (
          <div className="aoa-proposal-card__empty">{t('metaAgent.fileManager.noFilesAttached')}</div>
        ) : (
          <div className="aoa-fm__file-list">
            {initialAttachments.map((att) => {
              const marked = removedIds.has(att.attachment_id);
              const ctxState = resolveExistingContextState(att);
              // Folders have no per-file context flag — "always in context"
              // lives on the agent LINK there, so the toggle would be a lie.
              const showCtxToggle = !marked && targetType !== 'folder';
              return (
                <div
                  key={att.attachment_id}
                  className={`aoa-fm__file-row${marked ? ' is-marked' : ''}`}
                >
                  <div className="aoa-fm__file-icon">
                    <FileTypeIcon fileName={att.file_name} />
                  </div>
                  <div className="aoa-fm__file-info">
                    <span className="aoa-fm__file-name" dir="auto">{att.file_name}</span>
                    <span className="aoa-fm__file-meta">
                      {formatSize(att.file_size_bytes)}
                      {marked && <span className="aoa-fm__pill aoa-fm__pill--will-remove">{t('metaAgent.fileManager.willRemove')}</span>}
                    </span>
                  </div>
                  {showCtxToggle && (
                    <div className="aoa-fm__ctx-wrap">
                      <span className="aoa-fm__ctx-label">direct</span>
                      <Tooltip
                        title={
                          ctxState
                            ? t('metaAgent.fileManager.directContextOn')
                            : t('metaAgent.fileManager.directContextOff')
                        }
                        placement="top"
                        overlayClassName="aoa-tooltip"
                      >
                        <Switch
                          size="small"
                          checked={ctxState}
                          checkedChildren="ON"
                          unCheckedChildren="OFF"
                          onChange={(checked) => toggleExistingContext(att, checked)}
                          disabled={isResolved || busy}
                          aria-label={ctxState ? t('metaAgent.fileManager.contextOn') : t('metaAgent.fileManager.contextOff')}
                          className="aoa-fm__ctx-switch"
                        />
                      </Tooltip>
                    </div>
                  )}
                  <button
                    type="button"
                    className={`aoa-fm__file-action${marked ? ' is-undo' : ''}`}
                    onClick={() => toggleRemove(att.attachment_id)}
                    disabled={isResolved || busy}
                    aria-label={marked ? t('metaAgent.fileManager.undoRemove') : t('metaAgent.fileManager.removeOnApply')}
                    title={marked ? t('metaAgent.fileManager.undoRemove') : t('metaAgent.fileManager.removeOnApply')}
                  >
                    {marked ? (
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 12a9 9 0 1 0 3-6.7" strokeLinecap="round" strokeLinejoin="round" />
                        <polyline points="3 4 3 10 9 10" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" strokeLinejoin="round" />
                        <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* "Add new files" section — hidden in the resolved/read-only
          view. Once a proposal is decided, the staging slot adds
          nothing; the existing attachments above ARE the history. */}
      {!isResolved && (
        <div className="aoa-proposal-card__section">
          <div className="aoa-proposal-card__section-title">
            {t('metaAgent.fileManager.addNewFiles')} <span className="aoa-fm__count">({stagedFiles.length})</span>
          </div>

          {stagedFiles.length > 0 && (
            <div className="aoa-fm__file-list">
              {stagedFiles.map((f, i) => {
                const stagedCtx = resolveStagedContextState(f);
                return (
                  <div key={`${f.name}-${i}`} className="aoa-fm__file-row is-staged">
                    <div className="aoa-fm__file-icon">
                      <FileTypeIcon fileName={f.name} />
                    </div>
                    <div className="aoa-fm__file-info">
                      <span className="aoa-fm__file-name" dir="auto">{f.name}</span>
                      <span className="aoa-fm__file-meta">
                        {formatSize(f.size)}
                        <span className="aoa-fm__pill aoa-fm__pill--will-add">{t('metaAgent.fileManager.willAdd')}</span>
                        {stagedCtx && (
                          <span className="aoa-fm__pill aoa-fm__pill--will-toggle">in context</span>
                        )}
                      </span>
                    </div>
                    {targetType !== 'folder' && (
                    <div className="aoa-fm__ctx-wrap">
                      <span className="aoa-fm__ctx-label">direct</span>
                      <Tooltip
                        title={
                          stagedCtx
                            ? `Direct to context = ON. After upload this file will be inlined into the ${targetType}'s instruction at every turn.`
                            : `Direct to context = OFF. After upload this file will be retrieved on demand via knowledge_search (RAG). Click to inline as direct context.`
                        }
                        placement="top"
                        overlayClassName="aoa-tooltip"
                      >
                        <Switch
                          size="small"
                          checked={stagedCtx}
                          checkedChildren="ON"
                          unCheckedChildren="OFF"
                          onChange={(checked) => toggleStagedContext(f, checked)}
                          disabled={isResolved || busy}
                          aria-label={stagedCtx ? t('metaAgent.fileManager.contextOn') : t('metaAgent.fileManager.contextOff')}
                          className="aoa-fm__ctx-switch"
                        />
                      </Tooltip>
                    </div>
                    )}
                    <button
                      type="button"
                      className="aoa-fm__file-action"
                      onClick={() => removeStagedFile(i)}
                      disabled={isResolved || busy}
                      aria-label={t('metaAgent.fileManager.removeFromStaged')}
                      title={t('metaAgent.fileManager.removeFromStaged')}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" strokeLinejoin="round" />
                        <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <UploadArea
            uploading={busy}
            uploadProgress={uploadProgress}
            dragOver={dragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onFileSelect={stageFiles}
            accept={ALLOWED_EXTENSIONS.join(',')}
            multiple
            disabled={isResolved || busy}
            className="aoa-fm__upload-area"
          />
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message={t('metaAgent.fileManager.someOperationsFailed')}
          description={<pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{error}</pre>}
          showIcon
          style={{ marginTop: 12 }}
        />
      )}

      {!isResolved && (
        <div className="aoa-proposal-card__actions">
          {/* Match the rest of the AoA proposal cards: plain
              <button>s with .aoa-pcard-btn classes. AntD's Button was
              picking up brand-gradient overrides that made these look
              chunky and off-style. */}
          <button
            type="button"
            className="aoa-pcard-btn aoa-pcard-btn-ghost"
            onClick={handleReject}
            disabled={busy}
          >
            {t('vault.cancel')}
          </button>
          <button
            type="button"
            className="aoa-pcard-btn aoa-pcard-btn-primary"
            onClick={handleUploadAndApply}
            disabled={busy || !hasPendingChanges}
          >
            {busy
              ? '…'
              : (
                <>
                  Upload &amp; Apply
                  {hasPendingChanges && ` (+${stagedFiles.length} / −${removedIds.size})`}
                </>
              )}
          </button>
        </div>
      )}
    </div>
  );
};
